import React from "react";
import { branch } from "baobab-react/higher-order";
import autoBind from "react-autobind";
import { Alert, Button, NavItem } from "react-bootstrap";
import Modal from "react-responsive-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
	acknowledgeNotification,
	dismissNotification,
	fetchNotificationInbox,
	getActivityWorkflowWorkItemOpenContext,
	hideNotificationCenter,
	markAllNotificationsRead,
	markNotificationRead,
	refreshNotificationUnreadCount,
	showNotificationCenter,
	clearNotificationState
} from "../../actions/notificationActions";
import { navigateTo } from "../../actions/navActions";
import { handleOpenInNewTab } from "../../utils/utils";

const POLL_INTERVAL_MS = 60000;
const MIN_REFRESH_INTERVAL_MS = 10000;

class NotificationCenter extends React.PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			actionError: ""
		};
		this.refreshIntervalId = null;
		this.lastRefreshAt = 0;
		autoBind(this);
	}

	componentDidMount() {
		this.startPolling();
		if (typeof window !== "undefined") {
			window.addEventListener("focus", this.handleWindowFocus);
			document.addEventListener("visibilitychange", this.handleVisibilityChange);
		}
		this.refreshFromServer(true);
	}

	componentDidUpdate(prevProps) {
		const wasLoggedIn = !!(prevProps.user && prevProps.user.loggedIn);
		const isLoggedIn = this.isLoggedIn();
		if (!wasLoggedIn && isLoggedIn) {
			this.refreshFromServer(true);
		} else if (wasLoggedIn && !isLoggedIn) {
			clearNotificationState();
		}
	}

	componentWillUnmount() {
		this.stopPolling();
		if (typeof window !== "undefined") {
			window.removeEventListener("focus", this.handleWindowFocus);
			document.removeEventListener("visibilitychange", this.handleVisibilityChange);
		}
	}

	isLoggedIn() {
		return !!(this.props.user && this.props.user.loggedIn);
	}

	startPolling() {
		if (!this.refreshIntervalId) {
			this.refreshIntervalId = setInterval(this.refreshFromServer, POLL_INTERVAL_MS);
		}
	}

	stopPolling() {
		if (this.refreshIntervalId) {
			clearInterval(this.refreshIntervalId);
			this.refreshIntervalId = null;
		}
	}

	handleWindowFocus() {
		this.refreshFromServer(true);
	}

	handleVisibilityChange() {
		if (typeof document !== "undefined" && document.visibilityState === "visible") {
			this.refreshFromServer(true);
		}
	}

	refreshFromServer(force = false) {
		if (!this.isLoggedIn()) {
			return;
		}

		const now = Date.now();
		if (!force && now - this.lastRefreshAt < MIN_REFRESH_INTERVAL_MS) {
			return;
		}
		this.lastRefreshAt = now;

		const notifications = this.props.notifications || {};
		if (notifications.visible) {
			fetchNotificationInbox({ silent: true }).catch((err) => {
				console.warn("Failed to refresh notification inbox.", err);
			});
		} else {
			refreshNotificationUnreadCount().catch((err) => {
				console.warn("Failed to refresh notification unread count.", err);
			});
		}
	}

	handleOpenCenter(e) {
		if (e && e.preventDefault) {
			e.preventDefault();
		}
		this.setState({ actionError: "" });
		showNotificationCenter().catch((err) => {
			console.warn("Failed to open notification center.", err);
		});
	}

	handleCloseCenter() {
		hideNotificationCenter();
		this.setState({ actionError: "" });
	}

	handleRefresh() {
		this.setState({ actionError: "" });
		fetchNotificationInbox().catch((err) => {
			this.setState({ actionError: this.getErrorMessage(err) });
		});
	}

	handleMarkAllRead() {
		this.setState({ actionError: "" });
		markAllNotificationsRead().catch((err) => {
			this.setState({ actionError: this.getErrorMessage(err) });
		});
	}

	handleRead(notification) {
		this.setState({ actionError: "" });
		markNotificationRead(notification).catch((err) => {
			this.setState({ actionError: this.getErrorMessage(err) });
		});
	}

	handleDismiss(notification) {
		this.setState({ actionError: "" });
		dismissNotification(notification).catch((err) => {
			this.setState({ actionError: this.getErrorMessage(err) });
		});
	}

	handleAcknowledge(notification) {
		this.setState({ actionError: "" });
		acknowledgeNotification(notification).catch((err) => {
			this.setState({ actionError: this.getErrorMessage(err) });
		});
	}

	async handleOpenLink(notification) {
		this.setState({ actionError: "" });
		try {
			if (notification && !notification.readAt) {
				await markNotificationRead(notification);
			}
			await this.openNotificationLink(notification && notification.link);
			hideNotificationCenter();
		} catch (err) {
			this.setState({ actionError: this.getErrorMessage(err) });
		}
	}

	getErrorMessage(err) {
		if (err && err.response && err.response.body && err.response.body.error) {
			return err.response.body.error;
		}
		if (err && err.message) {
			return err.message;
		}
		return "Notification action failed.";
	}

	formatDate(value) {
		if (!value) {
			return "";
		}
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) {
			return value;
		}
		return date.toLocaleString();
	}

	isNotificationCenterLink(link) {
		return /^ambifi:\/\/notifications(?=\/|\?|#|$)/i.test(link || "");
	}

	getWorkItemIdFromLink(link) {
		const rawLink = `${link || ""}`.trim();
		let match = rawLink.match(/^ambifi:\/\/work-item\/([^/?#]+)/i);
		if (match) {
			return decodeURIComponent(match[1]);
		}

		match = rawLink.match(/^\/work-items\/([^/?#]+)\/open/i);
		if (match) {
			return decodeURIComponent(match[1]);
		}

		try {
			const parsedUrl = new URL(rawLink);
			match = parsedUrl.pathname.match(/^\/work-items\/([^/?#]+)\/open/i);
			return match ? decodeURIComponent(match[1]) : null;
		} catch (err) {
			return null;
		}
	}

	hasOpenLink(notification) {
		const link = notification && notification.link ? notification.link.trim() : "";
		return !!link && !this.isNotificationCenterLink(link);
	}

	async openNotificationLink(link) {
		if (!link || this.isNotificationCenterLink(link)) {
			return;
		}

		const workItemId = this.getWorkItemIdFromLink(link);
		if (workItemId) {
			const response = await getActivityWorkflowWorkItemOpenContext(workItemId);
			const launchContext = response && response.launchContext ? response.launchContext : {};
			if (launchContext.canOpen === false || (launchContext.lifecycle && launchContext.lifecycle.canOpen === false)) {
				throw new Error(launchContext.openReason || (launchContext.lifecycle && launchContext.lifecycle.reason) || "This work item cannot be opened.");
			}
			const targetUrl = launchContext.webUrl || launchContext.relativeUrl;
			if (!targetUrl) {
				throw new Error("This work item does not have an activity launch target yet.");
			}
			navigateTo(this.addWorkItemIdToTargetUrl(targetUrl, workItemId));
			return;
		}

		if (/^https?:\/\//i.test(link)) {
			handleOpenInNewTab(link);
		} else if (link.indexOf("/") === 0) {
			navigateTo(link);
		} else if (typeof window !== "undefined") {
			window.location.href = link;
		}
	}

	addWorkItemIdToTargetUrl(targetUrl, workItemId) {
		if (!targetUrl || !workItemId || targetUrl.indexOf("workItemId=") !== -1) {
			return targetUrl;
		}

		const separator = targetUrl.indexOf("?") === -1 ? "?" : "&";
		return `${targetUrl}${separator}workItemId=${encodeURIComponent(workItemId)}`;
	}

	getSeverityStyle(notification) {
		const severity = (notification && notification.severity ? notification.severity : "normal").toLowerCase();
		if (severity === "critical" || severity === "error") {
			return styles.severityCritical;
		}
		if (severity === "warning") {
			return styles.severityWarning;
		}
		if (severity === "success") {
			return styles.severitySuccess;
		}
		return styles.severityNormal;
	}

	renderNotification(notification) {
		const unread = !notification.readAt;
		const requiresAcknowledgement = !!notification.requiresAcknowledgement;
		const acknowledged = !!notification.acknowledgedAt;
		const title = notification.title || "Notification";
		const body = notification.body || "";
		const created = this.formatDate(notification.created || notification.createdAt);

		return (
			<div key={notification.recipientId || notification.notificationId} style={{ ...styles.item, ...(unread ? styles.itemUnread : {}) }}>
				<div style={styles.itemHeader}>
					<span style={{ ...styles.severityDot, ...this.getSeverityStyle(notification) }} />
					<div style={styles.itemTitleWrap}>
						<div style={styles.itemTitle}>{title}</div>
						<div style={styles.itemMeta}>
							{created}
							{notification.sourceType ? ` • ${notification.sourceType}` : ""}
							{requiresAcknowledgement && !acknowledged ? " • acknowledgement required" : ""}
						</div>
					</div>
				</div>
				{body && <div style={styles.itemBody}>{body}</div>}
				<div style={styles.itemActions}>
					{this.hasOpenLink(notification) && <Button bsSize="xsmall" onClick={() => this.handleOpenLink(notification)}><FontAwesomeIcon icon="external-link-alt" /> Open</Button>}
					{unread && <Button bsSize="xsmall" onClick={() => this.handleRead(notification)}><FontAwesomeIcon icon="check" /> Read</Button>}
					{requiresAcknowledgement && !acknowledged && <Button bsSize="xsmall" bsStyle="warning" onClick={() => this.handleAcknowledge(notification)}><FontAwesomeIcon icon="check-double" /> Acknowledge</Button>}
					<Button bsSize="xsmall" onClick={() => this.handleDismiss(notification)}><FontAwesomeIcon icon="times" /> Dismiss</Button>
				</div>
			</div>
		);
	}

	renderModal() {
		const notifications = this.props.notifications || {};
		const items = notifications.items || [];
		const loading = !!notifications.loading;
		const refreshing = !!notifications.refreshing;
		const error = notifications.error || this.state.actionError;

		return (
			<Modal open={!!notifications.visible} onClose={this.handleCloseCenter} classNames={{ modal: "custom-modal notification-center-modal" }}>
				<div style={styles.modal}>
					<div style={styles.modalHeader}>
						<div>
							<h3 style={styles.heading}>Notifications</h3>
							<div style={styles.subheading}>{notifications.unreadCount || 0} unread</div>
						</div>
						<div style={styles.headerActions}>
							<Button bsSize="small" onClick={this.handleRefresh} disabled={loading || refreshing}><FontAwesomeIcon icon={refreshing ? "spinner" : "sync"} spin={refreshing} /> Refresh</Button>
							<Button bsSize="small" onClick={this.handleMarkAllRead} disabled={loading || (notifications.unreadCount || 0) === 0}><FontAwesomeIcon icon="check" /> Read All</Button>
						</div>
					</div>
					{error && <Alert bsStyle="danger" style={styles.alert}>{error}</Alert>}
					{loading && items.length === 0 && <div style={styles.empty}>Loading notifications...</div>}
					{!loading && items.length === 0 && <div style={styles.empty}>No notifications.</div>}
					{items.length > 0 && <div style={styles.list}>{items.map((notification) => this.renderNotification(notification))}</div>}
				</div>
			</Modal>
		);
	}

	render() {
		if (!this.isLoggedIn()) {
			return null;
		}

		const notifications = this.props.notifications || {};
		const unreadCount = notifications.unreadCount || 0;
		const badgeText = unreadCount > 99 ? "99+" : `${unreadCount}`;
		const title = unreadCount > 0 ? `Notifications (${unreadCount})` : "Notifications";

		if (this.props.mobileMenuItem) {
			return (
				<React.Fragment>
					<div className="ambifiNavItem ambifiNavItemNotifications" title={title} onClick={this.handleOpenCenter}>
						<div className="ambifiNavItemHeader">
							<div className="ambifiNavItemIconWrap">
								<FontAwesomeIcon fixedWidth className="ambifiNavItemIcon" icon="bell" />
								{unreadCount > 0 && <span style={styles.mobileBadge}>{badgeText}</span>}
							</div>
							<div className="ambifiNavItemLabel">Notifications</div>
						</div>
					</div>
					{this.renderModal()}
				</React.Fragment>
			);
		}

		return (
			<React.Fragment>
				<NavItem style={styles.navItem} title={title} onClick={this.handleOpenCenter}>
					<span style={styles.bellWrap}>
						<FontAwesomeIcon icon="bell" />
						{unreadCount > 0 && <span style={styles.badge}>{badgeText}</span>}
					</span>
				</NavItem>
				{this.renderModal()}
			</React.Fragment>
		);
	}
}

const styles = {
	navItem: {
		marginTop: "8px",
		position: "relative"
	},
	bellWrap: {
		position: "relative",
		display: "inline-block",
		minWidth: "22px",
		textAlign: "center",
		fontSize: "18px"
	},
	badge: {
		position: "absolute",
		top: "-10px",
		right: "-14px",
		minWidth: "18px",
		height: "18px",
		padding: "0 5px",
		borderRadius: "9px",
		backgroundColor: "#d93636",
		color: "#ffffff",
		fontSize: "11px",
		fontWeight: "bold",
		lineHeight: "18px",
		textAlign: "center"
	},
	mobileBadge: {
		position: "absolute",
		top: "-9px",
		right: "-1px",
		minWidth: "18px",
		height: "18px",
		padding: "0 5px",
		borderRadius: "9px",
		backgroundColor: "#d93636",
		color: "#ffffff",
		fontSize: "11px",
		fontWeight: "bold",
		lineHeight: "18px",
		textAlign: "center",
		zIndex: 1
	},
	modal: {
		width: "min(720px, 90vw)",
		maxHeight: "80vh",
		display: "flex",
		flexDirection: "column"
	},
	modalHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: "16px",
		marginBottom: "12px"
	},
	heading: {
		margin: 0
	},
	subheading: {
		marginTop: "4px",
		color: "#667085",
		fontSize: "13px"
	},
	headerActions: {
		display: "flex",
		gap: "8px",
		flexWrap: "wrap",
		justifyContent: "flex-end"
	},
	alert: {
		marginBottom: "12px"
	},
	list: {
		overflowY: "auto",
		paddingRight: "4px"
	},
	empty: {
		padding: "32px 12px",
		color: "#667085",
		textAlign: "center"
	},
	item: {
		border: "1px solid #d9e2ec",
		borderRadius: "6px",
		padding: "12px",
		marginBottom: "10px",
		backgroundColor: "#ffffff"
	},
	itemUnread: {
		backgroundColor: "#f5fbff",
		borderColor: "#b7ddf5"
	},
	itemHeader: {
		display: "flex",
		alignItems: "flex-start",
		gap: "10px"
	},
	severityDot: {
		width: "10px",
		height: "10px",
		borderRadius: "5px",
		marginTop: "5px",
		flex: "0 0 auto"
	},
	severityNormal: {
		backgroundColor: "#4a90e2"
	},
	severityWarning: {
		backgroundColor: "#f0ad4e"
	},
	severityCritical: {
		backgroundColor: "#d93636"
	},
	severitySuccess: {
		backgroundColor: "#3c9b64"
	},
	itemTitleWrap: {
		minWidth: 0,
		flex: "1 1 auto"
	},
	itemTitle: {
		fontWeight: 600,
		fontSize: "15px",
		overflowWrap: "break-word"
	},
	itemMeta: {
		color: "#667085",
		fontSize: "12px",
		marginTop: "2px",
		overflowWrap: "break-word"
	},
	itemBody: {
		color: "#2f3a45",
		marginTop: "8px",
		overflowWrap: "break-word",
		whiteSpace: "pre-wrap"
	},
	itemActions: {
		display: "flex",
		flexWrap: "wrap",
		gap: "8px",
		marginTop: "10px"
	}
};

export default branch({
	user: ["user"],
	notifications: ["appState", "notifications"]
}, NotificationCenter);
